import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {AdyenPaymentMethodsService} from '../../../services/adyen/payment-methods'
import {paymentMethodsConfiguration} from '../pages/checkout/partials/paymentMethodsConfiguration'
import {AdyenEnvironmentService} from '@adyenPWA/lib/services/environment'
import {onPaymentsDetailsSuccess, onPaymentsSuccess} from '@adyenPWA/lib/contexts/helper'
import useShipToCountry from './../hooks/use-ship-to-country'

export const AdyenCheckoutContext = React.createContext({})

const AdyenCheckoutProvider = ({
    children,
    authToken,
    customerId,
    isCustomerRegistered,
    locale,
    site,
    adyenConfig,
    navigate,
    basket,
    returnUrl,
    page
}) => {
    const [fetchingPaymentMethods, setFetchingPaymentMethods] = useState(false)
    const [adyenPaymentMethods, setAdyenPaymentMethods] = useState()
    const [adyenEnvironment, setAdyenEnvironment] = useState()
    const [adyenStateData, setAdyenStateData] = useState()
    const [adyenPaymentInProgress, setAdyenPaymentInProgress] = useState()
    const [countryCode, setCountryCode] = useState()
    const callPaymentMethodsOnPages = ['checkout']
    const {shipToCountry} = useShipToCountry()
    const remainingAmount = basket?.c_basketTotalWithGiftCertificatesApplied

    useEffect(() => {
        const fetchEnvironment = async () => {
            const adyenEnvironmentService = new AdyenEnvironmentService(authToken, site)
            try {
                const data = await adyenEnvironmentService.fetchEnvironment()
                setAdyenEnvironment(data ? data : {error: true})
            } catch (error) {
                setAdyenEnvironment({error})
            }
        }
        fetchEnvironment()
    }, [])

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            if (remainingAmount <= 0) {
                setAdyenPaymentMethods({paymentMethods: []})
                setFetchingPaymentMethods(false)
                return
            }

            setFetchingPaymentMethods(true)
            const adyenPaymentMethodsService = new AdyenPaymentMethodsService(authToken, site)
            try {
                // We use countryCode from context if filled (after changing shipping address or country code of the billing address)
                // or the country code of the shipping address (at page loading for registered user)
                // or the shipToCountry from the context (for non registered users)
                const data = await adyenPaymentMethodsService.fetchPaymentMethods(
                    customerId,
                    locale,
                    countryCode ||
                        basket?.shipments[0]?.shippingAddress?.countryCode ||
                        shipToCountry.id,
                    basket?.basketId,
                    site,
                    false,
                    basket,
                    shipToCountry.id
                )
                setAdyenPaymentMethods(data ? data : {error: true})
                setFetchingPaymentMethods(false)
            } catch (error) {
                setAdyenPaymentMethods({error})
                setFetchingPaymentMethods(false)
            }
        }

        if (!fetchingPaymentMethods && callPaymentMethodsOnPages.includes(page)) {
            fetchPaymentMethods()
        }
    }, [
        basket?.basketId,
        basket?.shipments,
        countryCode,
        customerId,
        authToken,
        shipToCountry?.id,
        remainingAmount
    ])

    const getTranslations = () => {
        return adyenConfig?.translations && Object.hasOwn(adyenConfig?.translations, locale.id)
            ? adyenConfig?.translations
            : null
    }

    const getPaymentMethodsConfiguration = async ({
        adyenPaymentMethods,
        beforeSubmit = [],
        afterSubmit = [],
        beforeAdditionalDetails = [],
        afterAdditionalDetails = [],
        onError
    }) => {
        return paymentMethodsConfiguration({
            additionalPaymentMethodsConfiguration: adyenConfig?.paymentMethodsConfiguration,
            paymentMethods: adyenPaymentMethods?.paymentMethods,
            isCustomerRegistered,
            token: authToken,
            site,
            basket,
            returnUrl,
            customerId,
            onError: adyenConfig?.onError || onError,
            onNavigate: navigate,
            afterSubmit: [
                ...afterSubmit,
                ...(adyenConfig?.afterSubmit || []),
                onPaymentsSuccess(navigate)
            ],
            beforeSubmit: [...beforeSubmit, ...(adyenConfig?.beforeSubmit || [])],
            afterAdditionalDetails: [
                ...afterAdditionalDetails,
                ...(adyenConfig?.afterAdditionalDetails || []),
                onPaymentsDetailsSuccess(navigate)
            ],
            beforeAdditionalDetails: [
                ...beforeAdditionalDetails,
                ...(adyenConfig?.beforeAdditionalDetails || [])
            ]
        })
    }

    const value = {
        adyenEnvironment,
        adyenPaymentMethods,
        adyenStateData,
        adyenPaymentInProgress,
        locale,
        navigate,
        setAdyenPaymentInProgress: (data) => setAdyenPaymentInProgress(data),
        setAdyenStateData: (data) => setAdyenStateData(data),
        getPaymentMethodsConfiguration,
        getTranslations,
        fetchingPaymentMethods,
        setCountryCode: (data) => setCountryCode(data)
    }

    return <AdyenCheckoutContext.Provider value={value}>{children}</AdyenCheckoutContext.Provider>
}

AdyenCheckoutProvider.propTypes = {
    children: PropTypes.any,
    authToken: PropTypes.string,
    customerId: PropTypes.string,
    isCustomerRegistered: PropTypes.bool,
    locale: PropTypes.any,
    site: PropTypes.any,
    adyenConfig: PropTypes.any,
    navigate: PropTypes.any,
    basket: PropTypes.any,
    returnUrl: PropTypes.string,
    page: PropTypes.oneOf(['checkout', 'confirmation', 'redirect'])
}

export default AdyenCheckoutProvider
